import {
  AppState,
  Auth0Provider,
  AuthorizationParams,
  User,
} from "@auth0/auth0-react";
import { useSubmit } from "@certn/api";
import { Layout, SectionMessage } from "@certn/cascadia-react";
import {
  getRegionFromHost,
  getRegionFromLocalStorage,
  getRegionFromUrl,
  getRegionKeySource,
  isSupportedRegion,
  setRegionKeySource,
} from "@certn/utils";
import type { RegionKey } from "@certn/utils";
import { PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";

import { APIProviderWrapper } from "./APIProviderWrapper";

interface RegionSources {
  host: string | null | undefined;
  returnTo: string | null | undefined;
  urlPath: string | null | undefined;
  localStorage: string | null | undefined;
  auth0Claim: string | null | undefined;
}

function resolveRegion(sources: RegionSources): RegionKey | null {
  const candidates = [
    sources.host,
    sources.returnTo,
    sources.urlPath,
    sources.localStorage,
    sources.auth0Claim,
  ];
  for (const value of candidates) {
    if (!value) continue;
    const lower = value.toLowerCase();
    if (isSupportedRegion(lower)) return lower;
  }
  return null;
}

function isIgnoredPrimaryRegionClaim(claim: unknown): boolean {
  if (typeof claim !== "string" || !claim) return true;
  return !isSupportedRegion(claim.toLowerCase());
}

interface Props extends PropsWithChildren {
  domain: string;
  clientId: string;
  apiUrl: string;
  authorizationParams: AuthorizationParams;
}

export function AuthProvider(props: Props) {
  const navigate = useNavigate();

  const { domain, clientId, apiUrl, authorizationParams, children } = props;
  const { submit: submitLoginLog } = useSubmit(
    "/api/internal/self/data/log-user-login/"
  );

  const onRedirectCallback = (appState?: AppState, user?: User) => {
    const appReturnTo = appState?.returnTo;

    // When there is no appState.returnTo, the login was initiated externally
    // (e.g. password setup email, email verification). In that case the URL
    // region and localStorage were set by RegionProvider's default fallback,
    // not by user intent, so they must not override the Auth0 claim.
    const isExternalAuthFlow = !appReturnTo;

    const returnTo = appReturnTo || window.location.pathname;

    const primaryRegionClaim = user?.["https://certn.co/claims/primary_region"];

    if (user) {
      delete user["https://certn.co/claims/primary_region"];
    }

    submitLoginLog(user).catch((error) => {
      console.error("Failed to submit login log", error);
    });

    const [path, queryString] = returnTo.split("?");

    let redirectPath = path
      .split("/")
      .filter((part) => !isSupportedRegion(part))
      .join("/");

    if (redirectPath && !redirectPath.startsWith("/")) {
      redirectPath = "/" + redirectPath;
    } else if (!redirectPath) {
      redirectPath = "/";
    }

    if (queryString) {
      redirectPath = redirectPath + "?" + queryString;
    }

    // Normal flow priority: host > returnTo > URL path > localStorage > Auth0 claim.
    // External flow priority: host > Auth0 claim (URL/localStorage are unreliable).
    //
    // Inferred flow (RegionProvider injected a default prefix before login): the
    // URL and returnTo still contain that prefix, but it reflects a fallback—not
    // user intent—so skip returnTo/urlPath/localStorage when primary_region exists.
    const isInferredRegionalContext =
      !isExternalAuthFlow &&
      !isIgnoredPrimaryRegionClaim(primaryRegionClaim) &&
      getRegionKeySource() === "inferred";

    function normalFlowSources(): RegionSources {
      return {
        host: getRegionFromHost(window.location.hostname),
        returnTo: path.split("/").filter(Boolean)[0],
        urlPath: getRegionFromUrl(),
        localStorage: getRegionFromLocalStorage(),
        auth0Claim: primaryRegionClaim,
      };
    }

    const resolvedTargetRegion = resolveRegion(
      isExternalAuthFlow
        ? {
            host: getRegionFromHost(window.location.hostname),
            returnTo: null,
            urlPath: null,
            localStorage: null,
            auth0Claim: primaryRegionClaim,
          }
        : isInferredRegionalContext
          ? {
              host: getRegionFromHost(window.location.hostname),
              returnTo: null,
              urlPath: null,
              localStorage: null,
              auth0Claim: primaryRegionClaim,
            }
          : normalFlowSources()
    );

    if (resolvedTargetRegion) {
      const currentUrlRegion = window.location.pathname.split("/")[1];
      // navigate() won't work here — the API domain is baked in at page load.
      // A full reload lets the app re-initialize against the correct regional backend.
      if (currentUrlRegion !== resolvedTargetRegion) {
        localStorage.setItem("region-key", resolvedTargetRegion);
        setRegionKeySource("explicit");
        window.location.href = "/" + resolvedTargetRegion + redirectPath;
        return;
      }
    }

    // Login completed on the right region (no corrective redirect). Promote
    // any leftover `inferred` to `explicit` so a subsequent in-tab nav (e.g.
    // logout → typed cross-region URL) uses the normal priority chain.
    if (getRegionKeySource() === "inferred") {
      setRegionKeySource("explicit");
    }

    navigate(redirectPath);
  };

  if (!authorizationParams.audience || !clientId) {
    return (
      <Layout>
        <SectionMessage
          icon="error"
          type="danger"
          sectionTitle="Project setup failure"
        >
          Missing domain and/or clientId. Please add domain & clientId variables
          to your .env file.
        </SectionMessage>
      </Layout>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <APIProviderWrapper
        domain={apiUrl}
        authorizationParams={authorizationParams}
      >
        {children}
      </APIProviderWrapper>
    </Auth0Provider>
  );
}
